import { useState, useRef, useCallback, useEffect } from "react";
import { getTigerResource } from "../utils/tigerResource";

interface SplitPaneProps {
  left: React.ReactNode;
  right: React.ReactNode;
  initialWidth?: number;
  minWidth?: number;
  maxWidth?: number;
}

export function SplitPane({
  left,
  right,
  initialWidth = 220,
  minWidth = 120,
  maxWidth = 500,
}: SplitPaneProps) {
  const [width, setWidth] = useState(initialWidth);
  const [collapsed, setCollapsed] = useState(false);
  const dragging = useRef(false);
  const startX = useRef(0);
  const startWidth = useRef(0);

  const onMouseDown = useCallback((e: React.MouseEvent) => {
    if (collapsed) return; // no resize while folded away
    dragging.current = true;
    startX.current = e.clientX;
    startWidth.current = width;
    e.preventDefault();
  }, [width, collapsed]);

  useEffect(() => {
    const onMouseMove = (e: MouseEvent) => {
      if (!dragging.current) return;
      const delta = e.clientX - startX.current;
      const newWidth = Math.min(maxWidth, Math.max(minWidth, startWidth.current + delta));
      setWidth(newWidth);
    };

    const onMouseUp = () => {
      dragging.current = false;
    };

    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseup", onMouseUp);
    return () => {
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", onMouseUp);
    };
  }, [minWidth, maxWidth]);

  return (
    <div className="split-pane">
      <div className="split-pane-left" style={{ width: collapsed ? 0 : width }}>
        {left}
      </div>
      <div
        className="split-pane-handle"
        onMouseDown={onMouseDown}
        style={collapsed ? { cursor: "default" } : undefined}
      >
        <button
          type="button"
          className={`split-pane-fold${collapsed ? " is-collapsed" : ""}`}
          title={collapsed ? getTigerResource("TIGER_UI_SHOW_TREE") : getTigerResource("TIGER_UI_HIDE_TREE")}
          aria-label={collapsed ? getTigerResource("TIGER_UI_SHOW_TREE") : getTigerResource("TIGER_UI_HIDE_TREE")}
          aria-expanded={!collapsed}
          onMouseDown={(e) => e.stopPropagation()}
          onClick={() => setCollapsed((c) => !c)}
        >
          <svg
            className="split-pane-fold-icon"
            width="6"
            height="6"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="3"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <polyline points="15 18 9 12 15 6" />
          </svg>
        </button>
      </div>
      <div className="split-pane-right">
        {right}
      </div>
    </div>
  );
}
